import React, { useState, useEffect } from 'react';
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Plus,
  BarChart3,
  HelpCircle,
  FileText,
  Upload,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Save
} from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import FormStepper from '../common/FormStepper';
import { useHelp } from '../../context/HelpContext';
import PageHelp from '../help/PageHelp';
import ContractService from '../../services/contractService';
import AIService from '../../services/aiService';
import { useToast, ToastContainer } from '../common/Toast';

const ComplianceTracking = () => {
  const { openPageHelp } = useHelp();
  const { success, error: showError, toasts, removeToast } = useToast();
  const [activeTab, setActiveTab] = useState('active');
  // viewMode: 'list' | 'create' | 'edit' | 'view'
  const [viewMode, setViewMode] = useState('list');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCompliance, setSelectedCompliance] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [complianceData, setComplianceData] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiInsights, setAiInsights] = useState(null);
  const [formMode, setFormMode] = useState('create');
  const [formData, setFormData] = useState({
    contractId: '',
    complianceType: '',
    complianceCategory: '',
    obligationDescription: '',
    regulatoryReference: '',
    complianceStatus: 'pending',
    dueDate: '',
    evidenceNotes: '',
    riskLevel: 'medium',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [evidenceFile, setEvidenceFile] = useState(null);

  // Stepper state
  const [currentStep, setCurrentStep] = useState(0);

  const STEPS = [
    { id: 0, label: 'Compliance Details', icon: FileText, hint: 'Basic compliance information and contract' },
    { id: 1, label: 'Obligations & Requirements', icon: FileText, hint: 'Compliance obligations and regulatory references' },
    { id: 2, label: 'Risk Assessment', icon: AlertTriangle, hint: 'Risk level and due dates' },
    { id: 3, label: 'Evidence & Review', icon: Save, hint: 'Attach evidence and review details' }
  ];

  const transformComplianceRecord = (record, contractLookup = {}) => {
    const contract =
      record.contractId && typeof record.contractId === 'object'
        ? record.contractId
        : contractLookup[record.contractId] || {};
    const recordContractId =
      typeof record.contractId === 'object'
        ? record.contractId?._id
        : record.contractId;
    const contractIdentifier =
      contract.contractId || contract._id || recordContractId || '';
    const normalizedContractId = contractIdentifier
      ? String(contractIdentifier)
      : '';
    const contractTitle = contract.title || 'Untitled Contract';
    const contractStatus = contract.status || '';
    const dueDate = record.dueDate ? new Date(record.dueDate) : null;
    const isOverdue =
      record.isOverdue || (dueDate ? dueDate < new Date() : false);

    return {
      id: record._id || record.id,
      contractId: normalizedContractId,
      contractTitle,
      contractStatus,
      complianceType: record.complianceType,
      complianceCategory: record.complianceCategory,
      obligationDescription: record.obligationDescription,
      regulatoryReference: record.regulatoryReference || '',
      complianceStatus: record.complianceStatus,
      dueDate: record.dueDate,
      evidenceNotes: record.evidenceNotes || '',
      evidence: record.evidence || [],
      evidenceSubmittedAt: record.evidenceSubmittedAt || null,
      riskLevel: record.riskLevel,
      createdAt: record.createdAt,
      updatedAt: record.updatedAt,
      isOverdue,
      escalationStatus: record.escalationStatus || 'none',
      escalatedAt: record.escalatedAt || null,
      isReadOnly: contractStatus === 'Terminated' || contract.isActive === false
    };
  };

  const loadContracts = async () => {
    const response = await ContractService.getContracts({ includeInactive: true });
    const contractList = response.data || response || [];
    setContracts(contractList);
    return contractList;
  };

  const loadComplianceData = async () => {
    setLoading(true);
    try {
      const contractList = await loadContracts();
      if (!Array.isArray(contractList) || contractList.length === 0) {
        setComplianceData([]);
        return;
      }

      const contractLookup = contractList.reduce((acc, contract) => {
        const key = contract.contractId || contract.id;
        if (key) acc[key] = contract;
        return acc;
      }, {});

      const complianceResponses = await Promise.all(
        contractList.map(async (contract) => {
          const identifier = contract.contractId || contract.id;
          if (!identifier) return [];
          try {
            const result = await ContractService.getContractCompliance(identifier);
            return result?.data || [];
          } catch (error) {
            console.error('Error loading compliance for contract', identifier, error);
            return [];
          }
        })
      );

      const flattened = complianceResponses.flat();
      const transformedData = flattened.map((record) =>
        transformComplianceRecord(record, contractLookup)
      );
      setComplianceData(transformedData);

      if (activeTab === 'analytics') {
        try {
          const aiResponse = await AIService.generateInsights('compliance', '30d', {
            totalCompliance: transformedData.length,
            compliant: transformedData.filter((c) => c.complianceStatus === 'compliant').length,
            nonCompliant: transformedData.filter((c) => c.complianceStatus === 'non-compliant').length,
            overdue: transformedData.filter((c) => c.isOverdue).length,
          });
          setAiInsights(aiResponse);
        } catch (aiError) {
          console.error('Error generating AI insights:', aiError);
        }
      }
    } catch (error) {
      console.error('Error loading compliance data:', error);
      showError('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadComplianceData();
  }, [activeTab]);

  const stats = {
    totalCompliance: complianceData.length,
    compliant: complianceData.filter((c) => c.complianceStatus === 'compliant').length,
    nonCompliant: complianceData.filter((c) => c.complianceStatus === 'non-compliant').length,
    pending: complianceData.filter((c) => c.complianceStatus === 'pending').length,
    overdue: complianceData.filter((c) => c.isOverdue).length,
    averageComplianceScore:
      complianceData.length > 0
        ? Math.round(
            (complianceData.filter((c) => c.complianceStatus === 'compliant').length /
              complianceData.length) *
              100
          )
        : 0,
  };

  const getFilteredCompliance = () => {
    switch (activeTab) {
      case 'active': return complianceData.filter((c) => c.complianceStatus === 'compliant');
      case 'non-compliant': return complianceData.filter((c) => c.complianceStatus === 'non-compliant');
      case 'pending': return complianceData.filter((c) => c.complianceStatus === 'pending');
      default: return complianceData;
    }
  };

  const handleRefresh = () => {
    loadComplianceData();
    success('Compliance data refreshed');
  };

  // ─── Open full-page create form ───────────────────────────────────────────
  const handleAddNew = () => {
    if (contracts.length === 0) {
      showError('Create a contract before adding compliance records');
      return;
    }
    setSelectedCompliance(null);
    setFormMode('create');
    setFormData({
      contractId: '',
      complianceType: '',
      complianceCategory: '',
      obligationDescription: '',
      regulatoryReference: '',
      complianceStatus: 'pending',
      dueDate: '',
      evidenceNotes: '',
      riskLevel: 'medium',
    });
    setEvidenceFile(null);
    setCurrentStep(0);
    setViewMode('create');
  };

  const handleCancelForm = () => {
    setViewMode('list');
    setSelectedCompliance(null);
    setCurrentStep(0);
  };

  const handleStepClick = (stepIndex) => {
    if (stepIndex <= currentStep) setCurrentStep(stepIndex);
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep((prev) => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  // Header actions for list view
  const listHeaderActions = [
    {
      label: '',
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh compliance',
      disabled: loading,
    },
    {
      label: 'New Compliance',
      icon: <Plus size={18} />,
      onClick: handleAddNew,
      variant: 'primary',
    },
    {
      label: '',
      icon: <HelpCircle size={18} />,
      onClick: () => openPageHelp('compliance-tracking'),
      variant: 'outline',
      tooltip: 'Help',
    },
  ];

  const kpiCards = [
    { value: stats.totalCompliance, label: 'Total Compliance', icon: Shield, iconColor: '#2D7DD2', iconBg: '#D6EFFF' },
    { value: stats.compliant, label: 'Compliant', icon: CheckCircle, iconColor: '#059669', iconBg: '#D1FAE5' },
    { value: stats.nonCompliant, label: 'Non-Compliant', icon: XCircle, iconColor: '#EF4444', iconBg: '#FEE2E2' },
    { value: stats.pending, label: 'Pending Review', icon: Clock, iconColor: '#F59E0B', iconBg: '#FEF3C7' },
    { value: stats.overdue, label: 'Overdue Items', icon: AlertTriangle, iconColor: '#EF4444', iconBg: '#FEE2E2' },
    { value: `${Math.round(stats.averageComplianceScore)}%`, label: 'Avg. Compliance', icon: BarChart3, iconColor: '#7C3AED', iconBg: '#EDE9FE' },
  ];

  const columns = [
    {
      key: 'contractTitle',
      label: 'Contract',
      width: '240px',
      render: (value, row) => (
        <div>
          <div className="fw-medium">{value}</div>
          <div className="text-muted" style={{ fontSize: '12px' }}>{row.contractId}</div>
        </div>
      ),
    },
    {
      key: 'contractStatus',
      label: 'Contract Status',
      width: '140px',
      render: (status) => (
        <span className={`badge ${status === 'Terminated' ? 'badge-danger' : 'badge-success'}`}>
          {status || 'Unknown'}
        </span>
      ),
    },
    { key: 'complianceType', label: 'Type', width: '160px' },
    { key: 'complianceCategory', label: 'Category', width: '160px' },
    {
      key: 'complianceStatus',
      label: 'Status',
      width: '140px',
      render: (status) => {
        const statusConfig = {
          compliant: { className: 'badge-success', icon: <CheckCircle size={14} /> },
          'non-compliant': { className: 'badge-danger', icon: <XCircle size={14} /> },
          pending: { className: 'badge-warning', icon: <Clock size={14} /> },
        };
        const config = statusConfig[status] || statusConfig.pending;
        const label = status === 'non-compliant' ? 'Non-Compliant' : status === 'compliant' ? 'Compliant' : 'Pending';
        return (
          <span className={`badge ${config.className} d-flex align-items-center gap-1`}>
            {config.icon}{label}
          </span>
        );
      },
    },
    {
      key: 'riskLevel',
      label: 'Risk',
      width: '110px',
      render: (risk) => {
        const riskConfig = { high: 'badge-danger', medium: 'badge-warning', low: 'badge-success' };
        const label = risk ? risk.charAt(0).toUpperCase() + risk.slice(1) : 'Medium';
        return <span className={`badge ${riskConfig[risk] || 'badge-warning'}`}>{label}</span>;
      },
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      width: '140px',
      render: (value, row) => {
        if (!value) return '—';
        const dueDate = new Date(value);
        return (
          <div style={{ fontSize: '12px', color: row.isOverdue ? '#ef4444' : '#374151', fontWeight: row.isOverdue ? '500' : '400' }}>
            {dueDate.toLocaleDateString()}
            {row.isOverdue && <div style={{ fontSize: '10px' }}>Overdue</div>}
            {row.escalationStatus === 'escalated' && <div style={{ fontSize: '10px' }}>Escalated</div>}
          </div>
        );
      },
    },
  ];

  const handleView = (compliance) => {
    setSelectedCompliance(compliance);
    setFormMode('view');
    setFormData({
      contractId: compliance.contractId || '',
      complianceType: compliance.complianceType || '',
      complianceCategory: compliance.complianceCategory || '',
      obligationDescription: compliance.obligationDescription || '',
      regulatoryReference: compliance.regulatoryReference || '',
      complianceStatus: compliance.complianceStatus || 'pending',
      dueDate: compliance.dueDate ? new Date(compliance.dueDate).toISOString().split('T')[0] : '',
      evidenceNotes: compliance.evidenceNotes || '',
      riskLevel: compliance.riskLevel || 'medium',
    });
    setCurrentStep(0);
    setViewMode('view');
  };

  const handleEdit = (compliance) => {
    if (compliance.isReadOnly) {
      showError('Compliance records are read-only for terminated contracts');
      return;
    }
    setSelectedCompliance(compliance);
    setFormMode('edit');
    setFormData({
      contractId: compliance.contractId || '',
      complianceType: compliance.complianceType || '',
      complianceCategory: compliance.complianceCategory || '',
      obligationDescription: compliance.obligationDescription || '',
      regulatoryReference: compliance.regulatoryReference || '',
      complianceStatus: compliance.complianceStatus || 'pending',
      dueDate: compliance.dueDate ? new Date(compliance.dueDate).toISOString().split('T')[0] : '',
      evidenceNotes: compliance.evidenceNotes || '',
      riskLevel: compliance.riskLevel || 'medium',
    });
    setCurrentStep(0);
    setViewMode('edit');
  };

  const handleDelete = (compliance) => {
    setSelectedCompliance(compliance);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      if (selectedCompliance?.isReadOnly) {
        showError('Compliance records are read-only for terminated contracts');
        return;
      }
      if (selectedCompliance?.contractId && selectedCompliance?.id) {
        await ContractService.deleteContractCompliance(selectedCompliance.contractId, selectedCompliance.id);
        success('Compliance record deleted successfully');
        await loadComplianceData();
      }
      setShowDeleteModal(false);
      setSelectedCompliance(null);
    } catch (error) {
      console.error('Error deleting compliance:', error);
      showError('Failed to delete compliance record');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    const requiredFields = ['contractId', 'complianceType', 'complianceCategory', 'obligationDescription', 'complianceStatus', 'dueDate', 'riskLevel'];
    const missing = requiredFields.filter((field) => !formData[field]);
    if (missing.length > 0) {
      showError(`Missing required fields: ${missing.join(', ')}`);
      return false;
    }
    return true;
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    if (formMode === 'view') return;

    const selectedContract = contracts.find(
      (contract) => (contract.contractId || contract.id) === formData.contractId
    );
    const isReadOnly = selectedContract?.status === 'Terminated' || selectedContract?.isActive === false;

    if (isReadOnly) {
      showError('Compliance records are read-only for terminated contracts');
      return;
    }

    if (!validateForm()) return;

    if (selectedCompliance?.evidenceSubmittedAt && !String(formData.evidenceNotes || '').trim()) {
      showError('Evidence cannot be removed once submitted');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        complianceType: formData.complianceType,
        complianceCategory: formData.complianceCategory,
        obligationDescription: formData.obligationDescription,
        regulatoryReference: formData.regulatoryReference,
        complianceStatus: formData.complianceStatus,
        dueDate: formData.dueDate,
        evidenceNotes: formData.evidenceNotes,
        riskLevel: formData.riskLevel,
      };

      if (formMode === 'edit' && selectedCompliance?.id) {
        await ContractService.updateContractCompliance(formData.contractId, selectedCompliance.id, payload);
        success('Compliance record updated successfully');
      } else if (formMode === 'create') {
        await ContractService.createContractCompliance(formData.contractId, payload);
        success('Compliance record created successfully');
      }

      setViewMode('list');
      setSelectedCompliance(null);
      await loadComplianceData();
    } catch (error) {
      console.error('Error saving compliance record:', error);
      const message = error?.response?.data?.message || 'Failed to save compliance record';
      showError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const renderStepContent = (isReadOnly = false) => {
    const disabled = isReadOnly || formMode === 'view';
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contract *</label>
              <select
                value={formData.contractId}
                onChange={(e) => handleFormChange('contractId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={disabled}
              >
                <option value="">Select a contract</option>
                {contracts.map((contract) => {
                  const id = contract.contractId || contract.id;
                  return <option key={id} value={id}>{contract.title} ({id})</option>;
                })}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Compliance Type *</label>
              <input
                type="text"
                value={formData.complianceType}
                onChange={(e) => handleFormChange('complianceType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter compliance type"
                required
                disabled={disabled}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Compliance Category *</label>
              <input
                type="text"
                value={formData.complianceCategory}
                onChange={(e) => handleFormChange('complianceCategory', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter compliance category"
                required
                disabled={disabled}
              />
            </div>
          </div>
        );
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Obligation Description *</label>
              <textarea
                value={formData.obligationDescription}
                onChange={(e) => handleFormChange('obligationDescription', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Describe the compliance obligation"
                required
                disabled={disabled}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Regulatory Reference</label>
              <input
                type="text"
                value={formData.regulatoryReference}
                onChange={(e) => handleFormChange('regulatoryReference', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter regulatory reference"
                disabled={disabled}
              />
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Compliance Status</label>
                <select
                  value={formData.complianceStatus}
                  onChange={(e) => handleFormChange('complianceStatus', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={disabled}
                >
                  <option value="pending">Pending</option>
                  <option value="compliant">Compliant</option>
                  <option value="non-compliant">Non-Compliant</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Risk Level</label>
                <select
                  value={formData.riskLevel}
                  onChange={(e) => handleFormChange('riskLevel', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={disabled}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Due Date</label>
              <input
                type="date"
                value={formData.dueDate}
                onChange={(e) => handleFormChange('dueDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={disabled}
              />
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Evidence / Notes</label>
              <textarea
                value={formData.evidenceNotes}
                onChange={(e) => handleFormChange('evidenceNotes', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter evidence notes or compliance details"
                disabled={disabled}
              />
            </div>
            {!disabled && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Attach Evidence (Optional)</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center bg-gray-50 hover:bg-gray-100 transition-colors">
                  <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <div className="text-sm text-gray-600 mb-2">
                    <label htmlFor="file-upload" className="cursor-pointer text-blue-600 hover:text-blue-500 font-medium">
                      Upload a file
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">PDF, PNG, JPG up to 10MB</p>
                  <input id="file-upload" name="file-upload" type="file" className="sr-only" onChange={(e) => setEvidenceFile(e.target.files[0])} />
                </div>
                {evidenceFile && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-center">
                      <FileText className="h-5 w-5 text-blue-500 mr-2" />
                      <span className="text-sm text-blue-700">Selected: {evidenceFile.name}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Review Compliance Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-medium text-gray-700">Contract</p>
                  <p className="text-sm text-gray-900">{contracts.find(c => (c.contractId || c.id) === formData.contractId)?.title || 'Not selected'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Compliance Type</p>
                  <p className="text-sm text-gray-900">{formData.complianceType || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Category</p>
                  <p className="text-sm text-gray-900">{formData.complianceCategory || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Status</p>
                  <p className="text-sm text-gray-900">{formData.complianceStatus}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Risk Level</p>
                  <p className="text-sm text-gray-900">{formData.riskLevel}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Due Date</p>
                  <p className="text-sm text-gray-900">{formData.dueDate || 'Not specified'}</p>
                </div>
              </div>
              {formData.obligationDescription && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700">Obligation Description</p>
                  <p className="text-sm text-gray-900">{formData.obligationDescription}</p>
                </div>
              )}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const tabs = [
    { id: 'all', label: 'All Compliance', icon: Shield },
    { id: 'active', label: 'Active Compliance', icon: CheckCircle },
    { id: 'non-compliant', label: 'Non-Compliant', icon: XCircle },
    { id: 'pending', label: 'Pending Review', icon: Clock },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  ];

  const COLORS = ['#4678be', '#059669', '#F59E0B', '#EF4444', '#7C3AED', '#06B6D4'];

  const renderAnalytics = () => {
    const statusData = [
      { name: 'Compliant', value: stats.compliant },
      { name: 'Non-Compliant', value: stats.nonCompliant },
      { name: 'Pending', value: stats.pending },
    ];

    const typeMap = complianceData.reduce((acc, item) => {
      const type = item.complianceType || 'Unspecified';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});
    const typeData = Object.entries(typeMap).map(([name, value]) => ({ name, value }));

    const complianceTrends = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = date.toLocaleDateString('en-US', { month: 'short' });
      const monthCompliant = complianceData.filter((c) => {
        if (!c.createdAt) return false;
        const createdAt = new Date(c.createdAt);
        return createdAt.getMonth() === date.getMonth() && createdAt.getFullYear() === date.getFullYear() && c.complianceStatus === 'compliant';
      }).length;
      complianceTrends.push({ month: monthName, compliant: monthCompliant });
    }

    return (
      <div style={{ marginTop: '32px' }}>
        {aiInsights && (
          <div style={{ backgroundColor: '#EDE9FE', border: '1px solid #7C3AED', borderRadius: '0', padding: '20px', marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <BarChart3 size={24} style={{ color: '#7C3AED' }} />
              <h5 style={{ margin: 0, color: '#7C3AED', fontFamily: 'var(--font-family)', fontSize: '16px', fontWeight: '600' }}>AI-Powered Insights</h5>
            </div>
            <div style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-family)', fontSize: '14px', lineHeight: '1.6' }}>
              {aiInsights.insights || aiInsights.summary || 'Analyzing compliance trends and patterns...'}
            </div>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
          <div style={{ backgroundColor: 'white', border: '1px solid var(--border-color)', borderRadius: '0', padding: '24px' }}>
            <h5 style={{ marginBottom: '20px', color: 'var(--text-primary)', fontFamily: 'var(--font-family)', fontSize: '18px', fontWeight: '600' }}>Compliance by Status</h5>
            <div style={{ height: '300px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart>
                  <Pie data={statusData} cx="50%" cy="50%" labelLine={false} label={({ name, value }) => `${name}: ${value}`} outerRadius={80} fill="#8884d8" dataKey="value">
                    {statusData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '0', fontFamily: 'var(--font-family)' }} />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div style={{ backgroundColor: 'white', border: '1px solid var(--border-color)', borderRadius: '0', padding: '24px' }}>
            <h5 style={{ marginBottom: '20px', color: 'var(--text-primary)', fontFamily: 'var(--font-family)', fontSize: '18px', fontWeight: '600' }}>Compliance by Type</h5>
            <div style={{ height: '300px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={typeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" stroke="#6b7280" angle={-45} textAnchor="end" height={80} style={{ fontSize: '12px', fontFamily: 'var(--font-family)' }} />
                  <YAxis stroke="#6b7280" style={{ fontSize: '12px', fontFamily: 'var(--font-family)' }} />
                  <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '0', fontFamily: 'var(--font-family)' }} />
                  <Bar dataKey="value" fill="#4678be" name="Compliance" radius={[0, 0, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        <div style={{ backgroundColor: 'white', border: '1px solid var(--border-color)', borderRadius: '0', padding: '24px' }}>
          <h5 style={{ marginBottom: '20px', color: 'var(--text-primary)', fontFamily: 'var(--font-family)', fontSize: '18px', fontWeight: '600' }}>Compliance Trends (Last 6 Months)</h5>
          <div style={{ height: '300px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={complianceTrends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" stroke="#6b7280" style={{ fontSize: '12px', fontFamily: 'var(--font-family)' }} />
                <YAxis stroke="#6b7280" style={{ fontSize: '12px', fontFamily: 'var(--font-family)' }} />
                <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '0', fontFamily: 'var(--font-family)' }} />
                <Line type="monotone" dataKey="compliant" stroke="#4678be" strokeWidth={2} name="Compliant" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  };

  // ─── Full-page form view (create / edit / view) ───────────────────────────
  if (viewMode === 'create' || viewMode === 'edit' || viewMode === 'view') {
    const isViewOnly = viewMode === 'view';
    const isReadOnlyContract = (() => {
      const selectedContract = contracts.find(
        (contract) => (contract.contractId || contract.id) === formData.contractId
      );
      return selectedContract?.status === 'Terminated' || selectedContract?.isActive === false;
    })();

    const formTitle =
      viewMode === 'edit' ? 'Edit Compliance Record'
      : viewMode === 'view' ? 'Compliance Details'
      : 'New Compliance Record';

    const formDescription =
      viewMode === 'edit' ? 'Update the compliance record details'
      : viewMode === 'view' ? 'View compliance record details'
      : 'Create and configure a new compliance record';

    const saveLabel =
      viewMode === 'edit' ? 'Save Changes'
      : viewMode === 'view' ? 'Close'
      : 'Save Compliance';

    // Header actions for form page — Cancel + Save at top right (matching screenshot)
    const formHeaderActions = isViewOnly
      ? [
          {
            label: 'Close',
            onClick: handleCancelForm,
            variant: 'outline',
          },
        ]
      : [
          {
            label: 'Cancel',
            onClick: handleCancelForm,
            variant: 'outline',
          },
          {
            label: saveLabel,
            icon: isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />,
            onClick: () => {
              // Trigger form submit programmatically
              document.getElementById('compliance-form')?.requestSubmit();
            },
            variant: 'primary',
            disabled: isSaving || isReadOnlyContract,
          },
        ];

    return (
      <>
        {/* Breadcrumb with back navigation */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0 32px', marginBottom: '4px', marginTop: '16px', fontSize: '13px', color: 'var(--text-secondary)' }}>
          <button
            type="button"
            onClick={handleCancelForm}
            style={{ background: 'none', border: '1px solid var(--border-color)', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          >
            <ChevronLeft size={16} />
          </button>
          <span>Dashboard</span>
          <ChevronRight size={14} />
          <span>Contracts</span>
          <ChevronRight size={14} />
          <span>Compliance Tracking</span>
          <ChevronRight size={14} />
          <span style={{ color: 'var(--text-primary)', fontWeight: '500' }}>
            {viewMode === 'create' ? 'Create' : viewMode === 'edit' ? 'Edit' : 'View'}
          </span>
        </div>

        <PageHeader
          title={formTitle}
          description={formDescription}
          actions={formHeaderActions}
        />

        <div className="m-8 mt-0">
          {isReadOnlyContract && !isViewOnly && (
            <div style={{ backgroundColor: '#FEE2E2', padding: '10px 16px', marginBottom: '16px', color: '#991B1B', border: '1px solid #FCA5A5' }}>
              Compliance records are read-only for terminated contracts.
            </div>
          )}

          {/* Stepper + Form card */}
          <div style={{ backgroundColor: 'white', border: '1px solid var(--border-color)', padding: '32px' }}>
            <FormStepper steps={STEPS} currentStep={currentStep} onStepClick={handleStepClick} />

            <div style={{ marginTop: '32px' }}>
              <form id="compliance-form" onSubmit={handleFormSubmit}>
                {renderStepContent(isReadOnlyContract || isViewOnly)}

                {/* Step navigation buttons */}
                <div className="flex justify-between mt-8">
                  <button
                    type="button"
                    onClick={handleBack}
                    disabled={currentStep === 0}
                    className="flex items-center gap-2 px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={16} />
                    Back
                  </button>

                  <div className="flex gap-3">
                    {currentStep < STEPS.length - 1 ? (
                      <button
                        type="button"
                        onClick={handleNext}
                        className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Next
                        <ChevronRight size={16} />
                      </button>
                    ) : (
                      !isViewOnly && (
                        <button
                          type="submit"
                          disabled={isSaving || isReadOnlyContract}
                          className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {isSaving ? (
                            <><Loader2 size={16} className="animate-spin" />{viewMode === 'edit' ? 'Saving...' : 'Creating...'}</>
                          ) : (
                            <><Save size={16} />{saveLabel}</>
                          )}
                        </button>
                      )
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </>
    );
  }

  // ─── List view (default) ──────────────────────────────────────────────────
  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="Compliance Tracking"
        description="Monitor regulatory compliance, track violations, and manage corrective actions"
        actions={listHeaderActions}
      />

      {/* KPI Cards */}
      <div className="m-8">
        <div className="grid-modern-4 w-full gap-6">
          {kpiCards.map((card, index) => {
            const IconComponent = card.icon;
            return (
              <div key={index} className="relative border border-[var(--border-color)] bg-white">
                <div className="h-1 w-full bg-[var(--primary-color)]" />
                <div className="flex items-center gap-3 p-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center" style={{ background: card.iconBg }}>
                    <IconComponent size={20} style={{ color: card.iconColor }} />
                  </div>
                  <div>
                    <div className="text-[20px] font-bold text-[var(--text-primary)]">{card.value}</div>
                    <div className="mt-1 text-[13px] text-[var(--text-secondary)]">{card.label}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ width: '100%', maxWidth: '100%' }}>
        <div className="m-8 mt-0">
          {/* Tabs */}
          <div className="mb-8 border border-slate-200">
            <div className="flex items-center w-full bg-white border-b border-slate-200">
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center justify-center gap-2 px-6 py-3 w-full text-sm font-medium border-b-2 transition-colors duration-200 ${
                      isActive
                        ? 'border-[var(--primary-color)] text-[var(--primary-color)] bg-white'
                        : 'border-transparent text-[var(--text-secondary)] hover:bg-slate-100 hover:text-slate-800'
                    }`}
                  >
                    <IconComponent size={18} />
                    <span className="whitespace-nowrap">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Table content */}
          {(activeTab === 'active' || activeTab === 'non-compliant' || activeTab === 'pending' || activeTab === 'all') && (
            <div className="page-tab-content" style={{ marginTop: '32px' }}>
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', flexDirection: 'column', gap: '16px', backgroundColor: 'white', border: '1px solid var(--border-color)', padding: '40px' }}>
                  <div className="loading-spinner"></div>
                  <p style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-family)' }}>Loading compliance data...</p>
                </div>
              ) : (
                <div className="page-table-container" style={{ backgroundColor: 'white', border: '1px solid var(--border-color)' }}>
                  <DataTable
                    data={getFilteredCompliance()}
                    columns={columns}
                    title="Compliance Tracking"
                    searchable={true}
                    sortable={true}
                    exportable={true}
                    pagination={true}
                    pageSize={10}
                    showActions={true}
                    showCheckboxes={false}
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    searchPlaceholder="Search compliance by contract, type, or status..."
                    emptyMessage="No compliance records found. Click 'New Compliance' to get started."
                    initialHiddenColumns={['id']}
                  />
                </div>
              )}
            </div>
          )}

          {activeTab === 'analytics' && renderAnalytics()}
        </div>

        {/* Delete Modal */}
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Compliance Record"
          message={`Are you sure you want to delete the compliance record for "${selectedCompliance?.contractTitle || 'this contract'}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
          isLoading={isDeleting}
        />

        <PageHelp />
        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    </>
  );
};

export default ComplianceTracking;
